import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useUnreadCount } from '../../hooks/useUnreadCount';
import api from '../../utils/api';
import {
  Send, Plus, X, FileText, ChevronLeft, User, MessageSquare, Check, CheckCheck, Search, AlertCircle, Download
} from 'lucide-react';
import PlanBadge from '../common/PlanBadge';
import { determineProAccess, canAccessChat } from '../../utils/proAccess';

const MobileChat = () => {
  const { user } = useAuth();
  const { markChatAsRead, markChatAsVisited } = useUnreadCount();
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConversationList, setShowConversationList] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewChat, setShowNewChat] = useState(false);
  const [availableInvoices, setAvailableInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [firstMessage, setFirstMessage] = useState('');
  const [creatingChat, setCreatingChat] = useState(false);
  const [invoiceSearchQuery, setInvoiceSearchQuery] = useState('');
  const messagesEndRef = useRef(null);

  const currentUserId = user?.id ? String(user.id) : null;
  const accountOwnerId =
    user?.role === 'additional_user' && user?.parentUserId
      ? String(user.parentUserId)
      : currentUserId;
  const hasProAccess = useMemo(() => determineProAccess(user), [user]);
  const chatAllowed = useMemo(
    () => canAccessChat(user?.subscriptionPlan, user?.subscriptionStatus, user?.role),
    [user?.subscriptionPlan, user?.subscriptionStatus, user?.role]
  );
  const chatFeatureEnabled = hasProAccess || chatAllowed;

  const normalizeMessage = (message) => {
    if (!message) return null;
    const sender =
      message.sender ||
      message.user ||
      {};
    const senderId =
      message.senderId ||
      message.sender_id ||
      sender.id ||
      message.userId ||
      message.user_id ||
      null;

    const parentUserId =
      sender.parentUserId ||
      sender.parent_user_id ||
      message.parentUserId ||
      message.parent_user_id ||
      null;

    const content = message.content || message.message || message.text || '';
    const createdAt =
      message.createdAt ||
      message.created_at ||
      message.timestamp ||
      message.sentAt ||
      new Date().toISOString();

    return {
      ...message,
      sender: {
        ...sender,
        parentUserId: parentUserId
      },
      senderId,
      content,
      text: content,
      timestamp: message.timestamp || createdAt,
      createdAt,
      attachments: Array.isArray(message.attachments) ? message.attachments : [],
      isRead: message.isRead ?? message.is_read ?? false
    };
  };

  useEffect(() => {
    if (!chatFeatureEnabled) {
      setLoading(false);
      return;
    }
    fetchConversations();
    // Mark chat as visited when entering chat tab
    markChatAsVisited();
    // Silent refresh - don't show loading spinner during background polling
    const interval = setInterval(() => fetchConversations(true), 30000);
    return () => clearInterval(interval);
  }, [chatFeatureEnabled]);

  useEffect(() => {
    scrollToBottom();
  }, [activeConversation?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Mark conversation as read (local state update)
  const markAsRead = (conversationId) => {
    // Update local state to remove unread badge immediately
    setConversations(prev => 
      prev.map(conv => 
        conv.id === conversationId ? { ...conv, unreadCount: 0 } : conv
      )
    );
  };

  const fetchConversations = async (silentRefresh = false) => {
    try {
      // Only show loading spinner on initial load, not on background polling
      if (!silentRefresh) {
        setLoading(true);
      }
      setError(null);
      
      // Try to fetch conversations
      const response = await api.get('/chat');
      
      if (response.data) {
        const payload = response.data;
        let normalized = [];

        if (Array.isArray(payload)) {
          normalized = payload;
        } else if (Array.isArray(payload.conversations)) {
          normalized = payload.conversations;
        } else if (Array.isArray(payload.data)) {
          normalized = payload.data;
        } else if (payload.data && Array.isArray(payload.data.conversations)) {
          normalized = payload.data.conversations;
        } else if (payload.data && Array.isArray(payload.data.chats)) {
          normalized = payload.data.chats;
        } else if (Array.isArray(payload.chats)) {
          normalized = payload.chats;
        }

        setConversations(normalized);
      } else {
        setConversations([]);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
      
      // If it's a 404 or 500 error, the chat feature might not be set up yet
      if (error.response?.status === 404 || error.response?.status === 500) {
        setConversations([]);
        setError(null); // Don't show error for missing feature
      } else {
        setError('Failed to load conversations');
        setConversations([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableInvoices = async () => {
    try {
      const response = await api.get('/invoices');
      setAvailableInvoices(response.data?.data?.invoices || response.data?.invoices || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setAvailableInvoices([]);
    }
  };

  // Filter invoices based on search query
  const filteredInvoices = availableInvoices.filter(invoice => {
    if (!invoiceSearchQuery) return true;
    const searchLower = invoiceSearchQuery.toLowerCase();
    return (
      invoice.invoiceNumber?.toLowerCase().includes(searchLower) ||
      invoice.receiver?.companyName?.toLowerCase().includes(searchLower) ||
      invoice.receiver?.name?.toLowerCase().includes(searchLower) ||
      invoice.receiver?.email?.toLowerCase().includes(searchLower) ||
      invoice.sender?.companyName?.toLowerCase().includes(searchLower) ||
      invoice.sender?.name?.toLowerCase().includes(searchLower) ||
      invoice.total?.toString().includes(searchLower)
    );
  });

  const handleCreateNewChat = async () => {
    if (!chatFeatureEnabled) {
      alert('Chat is available on the Pro plan. Please upgrade to start new conversations.');
      return;
    }
    if (!selectedInvoice || !firstMessage.trim()) {
      alert('Please select an invoice and enter a message');
      return;
    }

    try {
      setCreatingChat(true);
      const response = await api.post('/chat', {
        invoiceId: selectedInvoice.id,
        initialMessage: firstMessage.trim()
      });

      if (response.data?.existingChat) {
        alert('Chat already exists! Opening existing conversation.');
      } else {
        alert('New chat created successfully!');
      }

      // Refresh conversations
      await fetchConversations();
      
      // Reset and close modal
      setShowNewChat(false);
      setSelectedInvoice(null);
      setFirstMessage('');
      setInvoiceSearchQuery('');
    } catch (error) {
      console.error('Error creating chat:', error);
      alert(error.response?.data?.error || 'Failed to create chat. Please try again.');
    } finally {
      setCreatingChat(false);
    }
  };

  // Filter conversations based on search
  const filteredConversations = conversations.filter(conversation => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    const otherParty = conversation.otherParticipant || conversation.otherParty || conversation.customer;
    return (
      otherParty?.name?.toLowerCase().includes(searchLower) ||
      otherParty?.companyName?.toLowerCase().includes(searchLower) ||
      conversation.invoiceNumber?.toLowerCase().includes(searchLower) ||
      conversation.invoice?.invoiceNumber?.toLowerCase().includes(searchLower) ||
      conversation.lastMessage?.toLowerCase().includes(searchLower)
    );
  });

  const handleFileDownload = async (attachment) => {
    try {
      console.log('📥 Downloading file attachment:', attachment);
      
      // Get download URL from backend with original filename
      const response = await api.get(`/api/files/download/${encodeURIComponent(attachment.s3Key)}?filename=${encodeURIComponent(attachment.name)}`);
      
      if (response.data.success) {
        // Create a temporary link to trigger download with original filename
        const link = document.createElement('a');
        link.href = response.data.downloadUrl;
        link.download = attachment.name; // Use original filename
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert('Failed to download file. Please try again.');
      }
    } catch (error) {
      console.error('Error downloading file attachment:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !activeConversation) return;

    try {
      const response = await api.post('/api/chat', {
        invoiceId: activeConversation.invoiceId,
        message: newMessage.trim()
      });
      
      const sentMessage = response.data?.chatMessage || response.data?.message;

      const optimisticMessage = {
        id: sentMessage?.id || Date.now(),
        content: sentMessage?.content || sentMessage?.message || sentMessage?.text || newMessage.trim(),
        senderId: sentMessage?.senderId || sentMessage?.sender_id || user?.id,
        sender: sentMessage?.sender || {
          id: user?.id,
          firstName: user?.firstName,
          lastName: user?.lastName,
          companyName: user?.companyName,
          parentUserId: user?.parentUserId
        },
        timestamp: sentMessage?.timestamp || new Date().toISOString(),
        createdAt: sentMessage?.timestamp || new Date().toISOString(),
        attachments: Array.isArray(sentMessage?.attachments) ? sentMessage.attachments : [],
        isRead: false,
        messageType: 'text'
      };

      const normalizedMessage = normalizeMessage(optimisticMessage);
      
      setActiveConversation(prev => ({
        ...prev,
        messages: [...(prev.messages || []), normalizedMessage]
      }));
      
      setNewMessage('');
      
      // Refresh conversations in background to update conversation list (silent)
      setTimeout(() => fetchConversations(true), 500);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!chatFeatureEnabled) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6 text-center space-y-4 max-w-md">
          <div className="flex justify-center">
            <MessageSquare className="w-10 h-10 text-yellow-600" />
          </div>
          <h2 className="text-xl font-semibold text-yellow-900">Chat is a Pro feature</h2>
          <p className="text-sm text-yellow-800">
            Upgrade to the Pro plan to message clients directly, share invoices, and resolve issues faster.
          </p>
          <button
            onClick={() => (window.location.href = '/pricing?plan=pro')}
            className="px-4 py-2 rounded-xl bg-yellow-600 text-white font-semibold hover:bg-yellow-700 transition"
          >
            Explore Pro plan
          </button>
        </div>
      </div>
    );
  }

  if (!showConversationList && activeConversation) {
    // Chat View
    return (
      <div className="fixed inset-0 bg-white flex flex-col z-50">
        {/* Chat Header */}
        <div className="bg-[#1F11ED] text-white px-4 py-3 flex items-center space-x-3">
          <button onClick={() => {
            setShowConversationList(true);
            setActiveConversation(null);
          }} className="p-2 hover:bg-white/10 rounded-lg">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <h2 className="font-semibold">
                {activeConversation.otherParticipant?.companyName || 
                 activeConversation.otherParticipant?.name || 
                 activeConversation.otherParty?.name || 
                 activeConversation.participant?.name || 
                 activeConversation.customer?.name || 
                 'Chat'}
              </h2>
              <PlanBadge plan={
                activeConversation.otherParticipant?.subscriptionPlan || 
                activeConversation.customer?.subscriptionPlan
              } />
            </div>
            <p className="text-xs text-white/80">
              {activeConversation.invoiceNumber || 
               activeConversation.invoice?.invoiceNumber || 
               'No Invoice'}
            </p>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
          {activeConversation && activeConversation.messages && Array.isArray(activeConversation.messages) && activeConversation.messages.length > 0 ? (
            activeConversation.messages.map((message, index) => {
              // Ultra-safe message rendering
              const safeMessage = normalizeMessage(message) || {};
              const messageContent = String(safeMessage.content || safeMessage.message || safeMessage.text || 'No content');
              const messageId = String(safeMessage.id || index);

              const senderIdRaw =
                safeMessage.senderId ||
                safeMessage.sender_id ||
                safeMessage.sender?.id ||
                safeMessage.userId ||
                safeMessage.user_id ||
                null;
              const senderId = senderIdRaw ? String(senderIdRaw) : null;
              const senderParentIdRaw =
                safeMessage.sender?.parentUserId ||
                safeMessage.sender?.parent_user_id ||
                null;
              const senderParentId = senderParentIdRaw ? String(senderParentIdRaw) : null;

              const ownsAsCurrent =
                (currentUserId && senderId === currentUserId) ||
                (currentUserId && senderParentId === currentUserId);

              const ownsAsAccount =
                (accountOwnerId && senderId === accountOwnerId) ||
                (accountOwnerId && senderParentId === accountOwnerId);

              const belongsToAccount = Boolean(ownsAsAccount);
              const senderEmailRaw =
                safeMessage.senderEmail ||
                safeMessage.sender?.email ||
                safeMessage.senderEmailAddress ||
                safeMessage.email ||
                null;
              const senderEmail = senderEmailRaw ? String(senderEmailRaw).toLowerCase() : null;
              const senderType = safeMessage.senderType || null;
              const messageDirection = safeMessage.direction || null;

              const otherIdentifiers = new Set();
              const otherEmails = new Set();

              const registerParticipant = (participant) => {
                if (!participant) return;
                const id = participant.id || participant.participantId || participant.userId;
                const parentId = participant.parentUserId || participant.parent_user_id;
                const email = participant.email || participant.emailAddress;
                if (id) otherIdentifiers.add(String(id));
                if (parentId) otherIdentifiers.add(String(parentId));
                if (email) otherEmails.add(String(email).toLowerCase());
              };

              registerParticipant(activeConversation.otherParticipant);
              registerParticipant(activeConversation.customer);
              registerParticipant(activeConversation.otherParty);

              const belongsToOther =
                (senderId && otherIdentifiers.has(senderId)) ||
                (senderParentId && otherIdentifiers.has(senderParentId)) ||
                (senderEmail && otherEmails.has(senderEmail));

              let isOwn = Boolean(ownsAsCurrent || ownsAsAccount);

              if (!isOwn && senderEmail) {
                const currentUserEmail = user?.email ? String(user.email).toLowerCase() : null;
                if (currentUserEmail && senderEmail === currentUserEmail) {
                  isOwn = true;
                }
              }

              if (!isOwn && senderType) {
                const typeLower = String(senderType).toLowerCase();
                if (['account', 'internal', 'team', 'user', 'owner'].some(key => typeLower.includes(key))) {
                  isOwn = true;
                } else if (['customer', 'client', 'external', 'receiver'].some(key => typeLower.includes(key))) {
                  isOwn = false;
                }
              }

              let externalDirection = false;
              if (messageDirection) {
                const dirLower = String(messageDirection).toLowerCase();
                if (['incoming', 'inbound', 'received'].some(key => dirLower.includes(key))) {
                  externalDirection = true;
                } else if (['outgoing', 'outbound', 'sent'].some(key => dirLower.includes(key))) {
                  isOwn = true;
                }
              }

              if (!isOwn && (belongsToOther || externalDirection)) {
                isOwn = false;
              } else if (!isOwn && !belongsToOther) {
                // Fallback to own side when sender can't be matched to the customer
                isOwn = true;
              }

              // Calculate display name and initials
              let displayName = 'Unknown';
              let initials = 'U';
              
              if (isOwn) {
                if (user?.firstName && user?.lastName) {
                  initials = `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
                  displayName = `${user.firstName} ${user.lastName}`;
                } else if (user?.firstName) {
                  initials = user.firstName[0].toUpperCase();
                  displayName = user.firstName;
                } else {
                  initials = 'Y';
                  displayName = 'You';
                }
              } else {
                const customerInfo =
                  safeMessage.sender ||
                  activeConversation.otherParticipant ||
                  activeConversation.customer ||
                  {};
                displayName = customerInfo?.companyName || customerInfo?.name || 
                            (customerInfo?.firstName && customerInfo?.lastName 
                              ? `${customerInfo.firstName} ${customerInfo.lastName}` 
                              : 'Customer');
                initials = (customerInfo?.companyName || customerInfo?.name || 'C').substring(0, 2).toUpperCase();
              }
              
              return (
                <div key={messageId} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                    isOwn 
                      ? 'bg-[#1F11ED] text-white' 
                      : 'bg-white border border-gray-200 text-gray-900'
                  }`}>
                    {/* Sender info for received messages */}
                    {!isOwn && (
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-xs font-medium text-gray-500">{displayName}</span>
                        <span className="inline-flex items-center justify-center w-4 h-4 text-xs font-medium text-gray-400 bg-gray-200 rounded-full">
                          {initials}
                        </span>
                      </div>
                    )}
                    
                    {/* Message text - only show if not just "[File attachment]" */}
                    {messageContent && messageContent !== '[File attachment]' && (
                      <p className="text-sm">{messageContent}</p>
                    )}
                    
                    {/* Attachments */}
                    {safeMessage.attachments && Array.isArray(safeMessage.attachments) && safeMessage.attachments.length > 0 && (
                      <div className="mt-2 space-y-2">
                        {safeMessage.attachments.map((attachment, attIndex) => (
                          <div 
                            key={attachment.id || attIndex}
                            className={`flex items-center justify-between p-2 rounded-lg ${
                              isOwn 
                                ? 'bg-white/10 border border-white/20' 
                                : 'bg-gray-100 border border-gray-200'
                            }`}
                          >
                            <div className="flex items-center space-x-2 flex-1 min-w-0">
                              <FileText className={`w-4 h-4 flex-shrink-0 ${isOwn ? 'text-white' : 'text-gray-600'}`} />
                              <div className="flex-1 min-w-0">
                                <p className={`text-xs truncate ${isOwn ? 'text-white' : 'text-gray-700'}`}>
                                  {attachment.name || 'File'}
                                </p>
                                {attachment.size && (
                                  <p className={`text-xs ${isOwn ? 'text-white/70' : 'text-gray-500'}`}>
                                    {(attachment.size / 1024).toFixed(1)} KB
                                  </p>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => handleFileDownload(attachment)}
                              className={`flex items-center space-x-1 px-2 py-1 rounded text-xs transition-colors flex-shrink-0 ml-2 ${
                                isOwn
                                  ? 'bg-white/20 hover:bg-white/30 text-white'
                                  : 'bg-blue-50 hover:bg-blue-100 text-blue-600'
                              }`}
                            >
                              <Download className="w-3 h-3" />
                              <span>Download</span>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Sender info for own messages */}
                    <div className="flex items-center justify-between mt-1">
                      {(safeMessage.createdAt || safeMessage.timestamp) && (
                        <p className={`text-xs ${isOwn ? 'text-white/70' : 'text-gray-500'}`}>
                          {formatTime(safeMessage.createdAt || safeMessage.timestamp)}
                        </p>
                      )}
                      
                      {/* Read Receipt for Own Messages */}
                      {isOwn && (
                        <div className="flex items-center gap-1 text-xs ml-2">
                          {safeMessage.isRead || safeMessage.is_read ? (
                            <>
                              <CheckCheck className="w-3 h-3 text-blue-300" />
                              <span className="text-blue-300">Read</span>
                            </>
                          ) : (
                            <Check className="w-3 h-3 text-white/70" />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-8">
              <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No messages yet</p>
              <p className="text-sm text-gray-400">Start the conversation!</p>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Message Input */}
        <div className="bg-white border-t border-gray-200 p-4">
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              placeholder="Type a message..."
              className="flex-1 px-4 py-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-[#1F11ED]"
            />
            <button
              onClick={handleSendMessage}
              disabled={!newMessage.trim()}
              className="p-3 bg-[#1F11ED] text-white rounded-full hover:bg-[#1709C7] disabled:opacity-50"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Conversations List
  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header with Search */}
      <div className="bg-white sticky top-0 z-10 shadow-sm">
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold text-gray-900">Messages</h1>
            <button
              onClick={() => {
                setShowNewChat(true);
                fetchAvailableInvoices();
              }}
              className="p-2 bg-[#1F11ED] text-white rounded-lg hover:bg-blue-700 active:scale-95 transition-all"
              title="New Chat"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
          
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-red-800">{error}</p>
              <button 
                onClick={fetchConversations}
                className="text-sm text-red-600 underline mt-1"
              >
                Try Again
              </button>
            </div>
          </div>
        )}
        
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-gray-200 rounded-xl animate-pulse"></div>
            ))}
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchQuery ? 'No conversations found' : 'No conversations yet'}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {searchQuery ? 'Try adjusting your search' : 'Start a conversation with a client'}
            </p>
            {!searchQuery && (
              <>
                <button
                  onClick={() => {
                    setShowNewChat(true);
                    fetchAvailableInvoices();
                  }}
                  className="bg-[#1F11ED] text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 active:scale-95 transition-all inline-flex items-center space-x-2"
                >
                  <Plus className="w-5 h-5" />
                  <span>Start New Chat</span>
                </button>
                <div className="mt-6 p-4 bg-blue-50 rounded-lg mx-4">
                  <p className="text-sm text-blue-800">
                    💡 <strong>Tip:</strong> Start a conversation about any invoice.
                  </p>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredConversations.map((conversation, index) => {
              // Ultra-safe rendering - no complex operations
              const safeConversation = conversation || {};
              // Backend returns otherParticipant (for additional users too)
              const safeCustomer = safeConversation.otherParticipant || safeConversation.customer || {};
              
              const displayName = String(
                safeCustomer.companyName ||
                safeCustomer.name || 
                (safeCustomer.firstName && safeCustomer.lastName ? 
                  `${safeCustomer.firstName} ${safeCustomer.lastName}` : 
                  'Unknown')
              );
              
              const invoiceNumber = String(
                safeConversation.invoiceNumber || 
                safeConversation.invoice?.invoiceNumber || 
                'No Invoice'
              );
              
              // Handle lastMessage - it might be an object
              let lastMessage = 'No messages yet';
              if (safeConversation.lastMessage) {
                if (typeof safeConversation.lastMessage === 'string') {
                  lastMessage = safeConversation.lastMessage;
                } else if (typeof safeConversation.lastMessage === 'object') {
                  // If it's an object, try to get the text/content/message property
                  lastMessage = safeConversation.lastMessage.text || 
                               safeConversation.lastMessage.content || 
                               safeConversation.lastMessage.message || 
                               'Click to view message';
                }
              }
              
              // Handle unread count - check multiple possible field names
              const unreadCount = Number(
                safeConversation.unreadCount || 
                safeConversation.unread_count || 
                safeConversation.unread || 
                0
              );
              
              const conversationId = String(safeConversation.id || safeConversation.invoiceId || index);

              return (
                <div
                  key={conversationId}
                  onClick={async () => {
                    try {
                      // Fetch full conversation messages
                      let chatId = safeConversation.id || safeConversation.chatId;
                      
                      // Try the messages endpoint first
                      try {
                        const messagesResponse = await api.get(`/api/chat/${chatId}/messages`);
                        const rawMessages = messagesResponse.data?.messages || messagesResponse.data || [];
                        const fullMessages = rawMessages
                          .map(normalizeMessage)
                          .filter(Boolean);

                        const updatedConversation = {
                          ...safeConversation,
                          messages: fullMessages
                        };
                        
                        setActiveConversation(updatedConversation);
                        setShowConversationList(false);
                        
                        // Mark conversation as read (local state)
                        markAsRead(safeConversation.id);
                        
                        // Mark messages as read in backend
                        try {
                          await fetch(`/api/chat/${chatId}/messages/mark-read`, {
                            method: 'POST',
                            headers: {
                              'Authorization': `Bearer ${localStorage.getItem('token')}`,
                              'Content-Type': 'application/json'
                            }
                          });
                          
                          // Update global unread count
                          markChatAsRead(chatId);
                        } catch (markReadError) {
                          console.error('Error marking messages as read:', markReadError);
                          // Non-critical error, don't block UI
                        }
                        
                        return;
                      } catch (endpointError) {
                        console.error('Error fetching messages:', endpointError);
                        // Fallback: use messages from conversation object
                        if (safeConversation.messages && Array.isArray(safeConversation.messages)) {
                          const normalizedFallback = safeConversation.messages
                            .map(normalizeMessage)
                            .filter(Boolean);

                          setActiveConversation({
                            ...safeConversation,
                            messages: normalizedFallback
                          });
                          setShowConversationList(false);
                          
                          // Still mark as read even on fallback
                          markAsRead(safeConversation.id);
                          return;
                        }
                        
                        // If no messages found, show empty state
                        setActiveConversation({
                          ...safeConversation,
                          messages: []
                        });
                        setShowConversationList(false);
                        return;
                      }
                    } catch (error) {
                      console.error('Error fetching conversation messages:', error);
                      // Fallback to conversation with no messages
                      setActiveConversation({
                        ...safeConversation,
                        messages: []
                      });
                      setShowConversationList(false);
                    }
                  }}
                  className={`bg-white rounded-xl shadow-sm p-4 active:bg-gray-50 cursor-pointer ${
                    unreadCount > 0 
                      ? 'border-2 border-[#1F11ED]' 
                      : 'border border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className={`font-semibold ${unreadCount > 0 ? 'text-gray-900' : 'text-gray-900'}`}>
                        {displayName}
                      </h3>
                      <p className="text-sm text-gray-600">{invoiceNumber}</p>
                    </div>
                    {unreadCount > 0 && (
                      <span className="w-7 h-7 bg-[#1F11ED] text-white rounded-full flex items-center justify-center text-xs font-bold shadow-md">
                        {unreadCount}
                      </span>
                    )}
                  </div>
                  
                  <p className={`text-sm truncate ${unreadCount > 0 ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>
                    {lastMessage}
                  </p>
                  
                  {safeConversation.lastMessageTime && (
                    <p className="text-xs text-gray-500 mt-2">
                      {new Date(safeConversation.lastMessageTime).toLocaleString('en-GB')}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* New Chat Modal */}
      {showNewChat && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center">
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            className="bg-white w-full sm:w-auto sm:min-w-[500px] rounded-t-2xl sm:rounded-2xl max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Start New Chat</h2>
              <button
                onClick={() => {
                  setShowNewChat(false);
                  setSelectedInvoice(null);
                  setFirstMessage('');
                  setInvoiceSearchQuery('');
                }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              {/* Select Invoice */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Invoice
                </label>
                
                {/* Invoice Search Bar */}
                {availableInvoices.length > 0 && (
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search invoices by number, client, or amount..."
                      value={invoiceSearchQuery}
                      onChange={(e) => setInvoiceSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent text-sm"
                    />
                    {invoiceSearchQuery && (
                      <button
                        onClick={() => setInvoiceSearchQuery('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
                
                {availableInvoices.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">No invoices available</p>
                    <p className="text-xs text-gray-500 mt-1">Create an invoice first to start a chat</p>
                  </div>
                ) : filteredInvoices.length === 0 ? (
                  <div className="text-center py-8">
                    <Search className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">No invoices found</p>
                    <p className="text-xs text-gray-500 mt-1">Try adjusting your search</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    <p className="text-xs text-gray-500 mb-2">
                      Showing {filteredInvoices.length} of {availableInvoices.length} invoices
                    </p>
                    {filteredInvoices.map((invoice) => (
                      <button
                        key={invoice.id}
                        onClick={() => setSelectedInvoice(invoice)}
                        className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                          selectedInvoice?.id === invoice.id
                            ? 'border-[#1F11ED] bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{invoice.invoiceNumber}</p>
                            <p className="text-sm text-gray-600">{invoice.receiver?.companyName || invoice.receiver?.name || 'Unknown'}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-gray-900">£{invoice.total?.toLocaleString() || 0}</p>
                            <p className="text-xs text-gray-500 capitalize">{invoice.status}</p>
                          </div>
                        </div>
                        {selectedInvoice?.id === invoice.id && (
                          <div className="mt-2 flex items-center text-[#1F11ED] text-sm">
                            <Check className="w-4 h-4 mr-1" />
                            Selected
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* First Message */}
              {selectedInvoice && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Message
                  </label>
                  <textarea
                    value={firstMessage}
                    onChange={(e) => setFirstMessage(e.target.value)}
                    placeholder="Type your message here..."
                    rows={4}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent resize-none"
                  />
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-2">
                <button
                  onClick={() => {
                    setShowNewChat(false);
                    setSelectedInvoice(null);
                    setFirstMessage('');
                    setInvoiceSearchQuery('');
                  }}
                  className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 active:scale-95 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateNewChat}
                  disabled={!selectedInvoice || !firstMessage.trim() || creatingChat}
                  className="flex-1 px-4 py-3 bg-[#1F11ED] text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed active:scale-95 transition-all inline-flex items-center justify-center space-x-2"
                >
                  {creatingChat ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <MessageSquare className="w-5 h-5" />
                      <span>Start Chat</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default MobileChat;

